const EXTERNAL_API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

const defaultHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (response.status >= 200 && response.status < 300) {
    return isJson ? await response.json() : await response.text();
  }

  let errorData;
  try {
    errorData = isJson ? await response.json() : await response.text();
  } catch {
    errorData = 'Unable to read response data';
  }

  const error = new Error(errorData?.message || 'Server error');
  error.status = response.status;
  error.statusText = response.statusText;
  error.data = errorData;

  switch (response.status) {
    case 400:
      error.message = errorData?.message || 'Invalid request';
      break;
    case 403:
      error.message = 'Access denied';
      break;
    case 404:
      error.message = 'Resource not found';
      break;
    case 500:
      error.message = 'Server error';
      break;
    default:
      error.message = `Error ${response.status}: ${response.statusText}`;
  }

  throw error;
};

const buildUrl = (endpoint, params) => {
  // For internal API routes (starting with /api/), use relative URL
  // For external APIs, use absolute URL
  const baseUrl = EXTERNAL_API_BASE_URL;
  const url = new URL(`${baseUrl}${endpoint}`);
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });
  }
  return url.toString();
};

const request = async (method, endpoint, { body, params, headers = {} } = {}) => {
  const url = buildUrl(endpoint, params);
  const options = {
    method,
    headers: { ...defaultHeaders, ...headers },
    credentials: 'omit', // Changed from 'include' to avoid CORS issues
  };

  if (body) options.body = JSON.stringify(body);

  try {
    const response = await fetch(url, options);
    return await handleResponse(response);
  } catch (error) {
    if (!error.status) {
      error.message = 'Failed to connect to server';
    }
    throw error;
  }
};

export const apiService = {
  get: (endpoint, options = {}) => request('GET', endpoint, options),
  post: (endpoint, data, options = {}) => request('POST', endpoint, { ...options, body: data }),
  put: (endpoint, data, options = {}) => request('PUT', endpoint, { ...options, body: data }),
  patch: (endpoint, data, options = {}) => request('PATCH', endpoint, { ...options, body: data }),
  delete: (endpoint, data, options = {}) => request('DELETE', endpoint, { ...options, body: data }),
};
